import { Button, Stack, Typography } from '@mui/material';
import { SharedDialog, DialogCommentField } from 'customer-management/customer-details/view-contracts/dialogs';
import { useViewContractsState } from 'customer-management/customer-details/view-contracts/state/view-contracts-state';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useSendContractNotification, useUpdateContract, useUpdateDraftContract } from 'shared/mutations';
import { useGetContract } from 'shared/queries';
import { ContractDocument, ContractRouteParams, I2PEnvironment } from 'shared/types';

interface RejectDialogProps {
  contract: ContractDocument;
  environment: I2PEnvironment;
}
export function RejectDialog({ contract, environment }: RejectDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const state = useViewContractsState();
  const updateDraftContractMutation = useUpdateDraftContract();
  const updateContractMutation = useUpdateContract();
  const notificationMutation = useSendContractNotification();

  const { contractId } = useParams<ContractRouteParams>();
  const { contract: contractToUpdate } = useGetContract(contractId);

  const onReject = useCallback(async () => {
    const comments = state.comments.value;
    if (contractToUpdate) {
      await updateContractMutation.mutateAsync({ ...contractToUpdate, approvalStatus: 'Rejected' });
    }

    contract.status = 'rejected';
    await updateDraftContractMutation.mutateAsync(contract);

    await notificationMutation.mutateAsync({
      contract,
      template: 'reject',
      comments,
      environment,
    });

    enqueueSnackbar('Contract Rejected', { variant: 'success' });

    state.merge({ rejectDialog: false, comments: '' });
  }, [
    contractToUpdate,
    updateContractMutation,
    updateDraftContractMutation,
    contract,
    state,
    notificationMutation,
    enqueueSnackbar,
    environment,
  ]);

  const loading = updateDraftContractMutation.isLoading || notificationMutation.isLoading;

  return (
    <SharedDialog
      open={state.rejectDialog.get()}
      title='Reject Contract Changes'
      onCancel={() => {
        state.merge({
          rejectDialog: false,
          comments: '',
        });
      }}
      submit={
        <Button onClick={onReject} disabled={loading} variant='contained' color='secondary'>
          reject
        </Button>
      }
    >
      <Stack spacing={1}>
        <Typography>You are about to Reject the changes to the contract.</Typography>
        <Typography>Please leave a comment on the reason for this rejection.</Typography>
        <DialogCommentField />
      </Stack>
    </SharedDialog>
  );
}
